import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaChevronLeft, FaChevronRight, FaPlus, FaTrashAlt } from "react-icons/fa";
import UnderlineInput from "../common/UnderlineInput";
import IsoDateInput, { formatIsoDay } from "../common/IsoDateInput";
import SelectDropdown from "../common/SelectDropdown";
import EntryPicker from "../common/EntryPicker";
import {
  AMOUNT_KEYS, FIELD_BY_KEY, RESIZABLE_WIDTHS,
  accountTypeLabel, columnDef, entryKindFor, isFieldEditable, offsetOnBankLine, isVatLine, layoutFor, lineId, money,
} from "./journalFields";
import useColumnWidths from "./useColumnWidths";

/**
 * Journal lines as ONE table — a row per line — instead of a three-column form
 * per line, so a request with many invoice lines stays scannable.
 *
 * Columns come from layoutFor(journal type): editable fields, the account Name,
 * and the Debit / Credit amounts. Dimensions, Sales tax and Invoice start folded
 * — one summary column each — and open with a click on the group header or a
 * summary cell. A group with a change not yet in ERP opens by itself, so its
 * "ERP: …" hint is never hidden.
 *
 * Lines built from the invoice / PO keep their amounts. Manual lines (added
 * here, while the journal is a draft) carry their own Debit or Credit and can
 * be removed; the server still refuses a journal that does not balance.
 */
const ENTRY_KIND = { invoice: 'invoice', txt: 'description', peb_txt: 'invoice_name' };
// Changing a type empties the code next to it: it belongs to another directory.
const CODE_FOR_TYPE = { account_type: 'account', offset_account_type: 'offset_account' };

const ChangeDot = ({ createOnly }) => (
  <span aria-hidden="true" className={`payment-journal-dot ${createOnly ? "payment-journal-dot-recreate" : ""}`} />
);

ChangeDot.propTypes = { createOnly: PropTypes.bool };

const typeOptions = (types, current) => {
  const options = types.map((t) => ({ value: t.value, label: t.label }));
  // A line saved with a type later removed from the master still shows it.
  return current && !options.some((o) => o.value === current) ? [...options, { value: current, label: current }] : options;
};

// The name picked with the code wins; otherwise the server's (AX / directory)
// name, but only while the code is still the one that name belongs to.
const accountNameOf = (line, values, erpLine) => {
  if (values.account_name) return values.account_name;
  const code = values.account ?? line.account;
  if (String(code || '') !== String(line.account || '')) return '';
  return line.account_name || erpLine?.account_name || '';
};

const FieldEditor = ({ field, line, values, accountTypes, taxGroups, editable, requestId, onChange }) => {
  const id = lineId(line);
  const label = `Line ${line.line_num} ${field.label}`;
  const set = (value) => onChange(id, field.key, value);

  if (field.taxGroup) {
    const current = values[field.key] || '';
    // Short label only ("VAT 10%"); the ERP code ("10") is what gets saved.
    const options = taxGroups.map((g) => ({ value: g.value, label: g.label || g.value }));
    if (current && !options.some((o) => o.value === current)) options.push({ value: current, label: current });
    return <SelectDropdown aria-label={label} value={current} disabled={!editable} options={options} allowClear placeholder="—"
      onChange={(value) => {
        set(value || '');
        // On the VAT line the input VAT account follows the rate (10% → 133102 …).
        const account = isVatLine(line) && taxGroups.find((g) => g.value === value)?.vat_account;
        if (account && account !== values.account) {
          onChange(id, 'account', account);
          onChange(id, 'account_name', '');
        }
      }} />;
  }

  if (field.accountType) {
    const current = values[field.key] || 'Ledger';
    return <SelectDropdown aria-label={label} value={current} disabled={!editable} options={typeOptions(accountTypes, current)}
      onChange={(value) => {
        set(value);
        if (value === current) return;
        onChange(id, CODE_FOR_TYPE[field.key], '');
        if (field.key === 'account_type') onChange(id, 'account_name', '');
      }} />;
  }
  if (field.date) {
    return <IsoDateInput aria-label={label} value={(values[field.key] || '').slice(0, 10)}
      disabled={!editable} onChange={set} />;
  }
  const kind = field.key === 'account' ? entryKindFor(accountTypes, values.account_type || 'Ledger')
    : field.key === 'offset_account' ? entryKindFor(accountTypes, values.offset_account_type || 'Ledger')
      : field.kind || ENTRY_KIND[field.key] || field.key;
  // Picking the account from the directory brings its name for the Name column;
  // typing a code by hand clears a name that no longer matches.
  const nameOnSelect = field.key === 'account'
    ? (entry) => onChange(id, 'account_name', entry?.context?.account_name || entry?.context?.vendor_name || '')
    : undefined;
  return <EntryPicker kind={kind} paymentRequestId={requestId} value={values[field.key] ?? ''} disabled={!editable}
    onChange={(value) => { set(value); if (field.key === 'account') onChange(id, 'account_name', ''); }}
    onSelect={nameOnSelect} aria-label={label} placeholder={field.wide ? 'Enter text…' : 'Search…'}
    title={field.wide ? values[field.key] || undefined : undefined} />;
};

FieldEditor.propTypes = {
  field: PropTypes.object.isRequired, line: PropTypes.object.isRequired, values: PropTypes.object.isRequired,
  accountTypes: PropTypes.array.isRequired, taxGroups: PropTypes.array.isRequired, editable: PropTypes.bool,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), onChange: PropTypes.func.isRequired,
};

// The Vendor line of an APP booked as in AX: its bank sits on the Bank payment line.
// Type Bank → the Offset account IS that line's bank (both cells show and change one
// value). Another type → the line books against it directly; the Bank payment line goes
// on Save. `offset_detached` marks that choice until it is saved (2026-10-22).
const BankOffsetEditor = ({ field, line, values, bankLine, bankValues, accountTypes, editable, requestId, onChange }) => {
  const id = lineId(line);
  const detached = !!values.offset_detached;
  const label = `Line ${line.line_num} ${field.label}`;
  if (field.key === 'offset_account_type') {
    const current = detached ? (values.offset_account_type || 'Ledger') : 'Bank';
    return <SelectDropdown aria-label={label} value={current} disabled={!editable} options={typeOptions(accountTypes, current)}
      onChange={(type) => {
        if (type === current) return;
        onChange(id, 'offset_detached', type !== 'Bank');
        onChange(id, 'offset_account_type', type === 'Bank' ? '' : type);
        onChange(id, 'offset_account', '');
      }} />;
  }
  if (detached) {
    return <EntryPicker kind={entryKindFor(accountTypes, values.offset_account_type || 'Ledger')} paymentRequestId={requestId}
      value={values.offset_account || ''} disabled={!editable} aria-label={label} placeholder="Search…"
      onChange={(value) => onChange(id, 'offset_account', value)} />;
  }
  const bankId = bankLine ? lineId(bankLine) : null;
  return <EntryPicker kind={entryKindFor(accountTypes, 'Bank')} paymentRequestId={requestId}
    value={(bankValues?.account ?? bankLine?.account ?? line.offset_bank_account) || ''} disabled={!editable || !bankId}
    aria-label={label} placeholder="Search…"
    onChange={(value) => { onChange(bankId, 'account', value); onChange(bankId, 'account_name', ''); }} />;
};

BankOffsetEditor.propTypes = {
  field: PropTypes.object.isRequired, line: PropTypes.object.isRequired, values: PropTypes.object.isRequired,
  bankLine: PropTypes.object, bankValues: PropTypes.object, accountTypes: PropTypes.array.isRequired, editable: PropTypes.bool,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), onChange: PropTypes.func.isRequired,
};

const ReadOnlyValue = ({ field, line, accountTypes }) => (offsetOnBankLine(line, field) ? (
  <span className="payment-journal-readonly" title={`The bank is on line ${line.offset_by_bank_line} (Bank payment) — change it in that line's Account`}>
    {field.key === "offset_account"
      ? `→ Line ${line.offset_by_bank_line} · ${line.offset_bank_account || "Bank payment"}`
      : "On the Bank payment line"}
  </span>
) : (
  <span className="payment-journal-readonly" title="Follows the vendor / employee on the journal header">
    {field.key === 'account_type' ? accountTypeLabel(accountTypes, line.account_type)
      : (field.date ? formatIsoDay(line[field.key]) : line[field.key]) || '—'}
  </span>
));

ReadOnlyValue.propTypes = { field: PropTypes.object.isRequired, line: PropTypes.object.isRequired, accountTypes: PropTypes.array.isRequired };

const FieldCell = ({ field, width, line, values, erpLine, change, createOnly, accountTypes, taxGroups, editable, requestId, onChange, nameUnder,
  bankLine, bankValues }) => {
  // Under the code when the layout has no Name column; the offset account always.
  const note = field.key === 'offset_account' ? (erpLine?.offset_account_name || line.offset_account_name)
    : field.key === 'account' && nameUnder ? accountNameOf(line, values, erpLine) : '';
  const shownFrom = field.date ? formatIsoDay(change?.from) : change?.from;
  const required = missingRequired(line, values).includes(field.key);
  return (
    <td className={`payment-journal-cell ${required ? 'payment-journal-cell-required' : ''}`}
      style={width ? { width, minWidth: width, maxWidth: width } : { minWidth: field.width }}>
      {isFieldEditable(line, field)
        ? <FieldEditor field={field} line={line} values={values} accountTypes={accountTypes} taxGroups={taxGroups} editable={editable} requestId={requestId} onChange={onChange} />
        : offsetOnBankLine(line, field) && editable
          ? <BankOffsetEditor field={field} line={line} values={values} bankLine={bankLine} bankValues={bankValues}
            accountTypes={accountTypes} editable={editable} requestId={requestId} onChange={onChange} />
          : <ReadOnlyValue field={field} line={line} accountTypes={accountTypes} />}
      {offsetOnBankLine(line, field) && editable && field.key === 'offset_account' && (
        <span className="payment-journal-cell-note">
          {values.offset_detached
            ? `Line ${line.offset_by_bank_line} (Bank payment) is removed on Save`
            : `Bank of line ${line.offset_by_bank_line} (Bank payment)`}
        </span>
      )}
      {change && (
        <span className="payment-journal-cell-note" title={`ERP: ${shownFrom || "—"}`}>
          <ChangeDot createOnly={createOnly} />ERP: {shownFrom || "—"}
        </span>
      )}
      {note && <span className="payment-journal-cell-note" title={note}>{note}</span>}
      {required && (
        <span className="payment-journal-cell-note payment-journal-cell-required-note"
          title={`AX will not post this line without ${field.label} for account ${values.account || line.account}`}>
          Required by AX
        </span>
      )}
    </td>
  );
};

FieldCell.propTypes = {
  field: PropTypes.object.isRequired, width: PropTypes.number, line: PropTypes.object.isRequired, values: PropTypes.object.isRequired,
  erpLine: PropTypes.object, change: PropTypes.object, createOnly: PropTypes.bool, accountTypes: PropTypes.array.isRequired,
  taxGroups: PropTypes.array.isRequired, editable: PropTypes.bool,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), onChange: PropTypes.func.isRequired, nameUnder: PropTypes.bool,
  bankLine: PropTypes.object, bankValues: PropTypes.object,
};

const COLUMN_NAME_WIDTH = columnDef('account_name').width;

// The Vendor line's account IS the vendor of the journal: picking it here sets the
// journal's vendor (checked against AX on Save), not a free line override.
const VendorAccountCell = ({ line, counterparty, width }) => (
  <td className="payment-journal-cell" style={width ? { width, minWidth: width, maxWidth: width } : { minWidth: 150 }}>
    <EntryPicker kind="vendor_account" paymentRequestId={counterparty.requestId} value={counterparty.value || ''}
      disabled={!counterparty.editable} aria-label={`Line ${line.line_num} Vendor account`}
      placeholder={counterparty.placeholder || 'Vendor code in AX…'}
      onChange={(value) => counterparty.onChange(value, '')}
      onSelect={(entry) => counterparty.onChange(entry.value, entry.context?.vendor_name || '')} />
    {!counterparty.value && <span className="payment-journal-cell-note payment-journal-cell-required">Pick the vendor (AX code)</span>}
    {counterparty.value && !counterparty.name && <span className="payment-journal-cell-note">Checked in AX on Save</span>}
  </td>
);

VendorAccountCell.propTypes = { line: PropTypes.object.isRequired, counterparty: PropTypes.object.isRequired, width: PropTypes.number };

const nameCellStyle = (width) => (width ? { width, minWidth: width, maxWidth: width } : { minWidth: COLUMN_NAME_WIDTH, maxWidth: 260 });

const NameCell = ({ line, values, erpLine, width }) => {
  const name = accountNameOf(line, values, erpLine);
  return (
    <td className="payment-journal-cell payment-journal-name-cell" style={nameCellStyle(width)}>
      <span className={`payment-journal-readonly ${name ? "" : "payment-journal-name-empty"}`} title={name || undefined}>{name || '—'}</span>
    </td>
  );
};


NameCell.propTypes = { line: PropTypes.object.isRequired, values: PropTypes.object.isRequired, erpLine: PropTypes.object, width: PropTypes.number };

// A folded group: what is filled, at a glance, and a click away from editing.
const SummaryCell = ({ group, line, values, changes, createOnly, onExpand }) => {
  const keys = group.keys.filter((key) => FIELD_BY_KEY[key] && isFieldEditable(line, FIELD_BY_KEY[key]));
  const filled = keys.map((key) => values[key]).filter((v) => String(v ?? '').trim());
  const changed = changes.filter((c) => group.keys.includes(c.field));
  const text = filled.length ? filled.map((v) => String(v).slice(0, 10)).join(' · ') : 'Not filled';
  return (
    <td className="payment-journal-cell payment-journal-summary-cell">
      <button type="button" onClick={onExpand} title={`Show ${group.label} — ${filled.length}/${keys.length} filled`}
        aria-label={`Show ${group.label} for line ${line.line_num}`} className="payment-journal-summary">
        {changed.length > 0 && <ChangeDot createOnly={changed.some((c) => createOnly.includes(c.field))} />}
        <span className={`payment-journal-summary-text ${filled.length ? "" : "payment-journal-summary-empty"}`}>{text}</span>
        <span className="payment-journal-summary-count">{filled.length}/{keys.length}</span>
      </button>
    </td>
  );
};

SummaryCell.propTypes = {
  group: PropTypes.object.isRequired, line: PropTypes.object.isRequired, values: PropTypes.object.isRequired,
  changes: PropTypes.array.isRequired, createOnly: PropTypes.array.isRequired, onExpand: PropTypes.func.isRequired,
};

// One row: number · label (cut to one line, full text on hover) · remove. The ERP
// voucher, and the date only when it differs from the journal's, go underneath.
const LineCell = ({ line, erpLine, onRemove, journalDate }) => {
  const voucher = erpLine?.voucher || line.voucher;
  const lineDate = (erpLine?.trans_date || line.trans_date)?.slice(0, 10);
  const date = lineDate && lineDate !== journalDate ? lineDate : null;
  const name = line.label || line.account_type;
  return (
    <th scope="row" className="payment-journal-cell payment-journal-line-cell">
      <div className="payment-journal-line-head">
        <span className="payment-journal-line-no">{line.line_num}</span>
        <span className="payment-journal-line-name" title={name}>{name}</span>
        {onRemove && (
          <button type="button" onClick={onRemove} className="payment-journal-line-remove"
            aria-label={`Remove line ${line.line_num}`}
            title={line.manual ? "Remove this manual line" : "Take this line out of the journal (it can be restored below)"}>
            <FaTrashAlt aria-hidden="true" size={11} />
          </button>
        )}
      </div>
      {(voucher || date) && (
        <span className="payment-journal-cell-note payment-journal-line-note">
          {voucher && <span title="Voucher">{voucher}</span>}
          {voucher && date && ' · '}
          {date && <span title="Transaction date">{date}</span>}
        </span>
      )}
      {erpLine === null && <span className="payment-journal-cell-note payment-journal-cell-warn">Not in the ERP journal</span>}
    </th>
  );
};

LineCell.propTypes = { line: PropTypes.object.isRequired, erpLine: PropTypes.object, onRemove: PropTypes.func, journalDate: PropTypes.string };

const AmountCell = ({ line, side, values, editable, onChange }) => {
  if (line.manual && editable) {
    return (
      <td className="payment-journal-cell payment-journal-num payment-journal-amount-input">
        <UnderlineInput type="number" min="0" step="any" inputMode="decimal" aria-label={`Line ${line.line_num} ${side === 'debit' ? 'Debit' : 'Credit'}`}
          value={values[side] ?? ''} placeholder="0"
          onChange={(event) => {
            onChange(lineId(line), side, event.target.value);
            // One side per line: typing a debit clears the credit, and back.
            if (event.target.value) onChange(lineId(line), side === 'debit' ? 'credit' : 'debit', '');
          }} />
      </td>
    );
  }
  const amount = line.manual ? values[side] : line[side];
  return <td className={`payment-journal-cell payment-journal-num ${Number(amount) ? "" : "payment-journal-num-empty"}`}>{money(amount)}</td>;
};

AmountCell.propTypes = {
  line: PropTypes.object.isRequired, side: PropTypes.oneOf(AMOUNT_KEYS).isRequired, values: PropTypes.object.isRequired,
  editable: PropTypes.bool, onChange: PropTypes.func.isRequired,
};

// A group holding a change not yet in ERP opens on its own; a click by the
// viewer (folding or opening) wins from then on.
const hasPending = (group, lines, pending) =>
  pending.some((c) => group.keys.includes(c.field) && lines.some((l) => l.line_num === c.line_num));

// Dimensions AX requires for the line's account (server rule) that are still empty.
export const missingRequired = (line, lineValues = {}) => (line.required_dimensions || [])
  .filter((key) => !String(lineValues[key] ?? line[key] ?? '').trim());

const JournalLinesTable = ({
  journalType, lines, values, erpLines, pending, createOnly, accountTypes, taxGroups, editable, allowEditLines, requestId,
  onChange, onAddLine, onRemoveLine, counterparty,
}) => {
  const [toggled, setToggled] = useState({});
  const { widths, handleProps } = useColumnWidths(RESIZABLE_WIDTHS);
  const { pinnedAmounts, groups } = layoutFor(journalType);
  // A group with a required dimension still empty opens by itself, so the gap is seen.
  const lacksRequired = (group) => lines.some((l) => missingRequired(l, values[lineId(l)]).some((k) => group.keys.includes(k)));
  const open = Object.fromEntries(groups.filter((g) => g.collapsible)
    .map((g) => [g.id, toggled[g.id] ?? (hasPending(g, lines, pending) || lacksRequired(g))]));
  const toggle = (id) => setToggled((state) => ({ ...state, [id]: !open[id] }));
  const columnsOf = (group) => (!group.collapsible || open[group.id] ? group.keys : null);
  const hasNameColumn = groups.some((g) => (columnsOf(g) || []).includes('account_name'));

  // Every column after Line, in order — the body, header and Total row share it.
  const columns = [
    ...(pinnedAmounts ? AMOUNT_KEYS.map((key) => ({ id: key, kind: 'amount', key, pinned: true })) : []),
    ...groups.flatMap((group) => {
      const keys = columnsOf(group);
      if (!keys) return [{ id: `${group.id}:summary`, kind: 'summary', group }];
      return keys.map((key) => ({ id: key, key, group,
        kind: AMOUNT_KEYS.includes(key) ? 'amount' : key === 'account_name' ? 'name' : 'field' }));
    }),
  ];

  const amountOf = (line, side) => Number(line.manual ? values[lineId(line)]?.[side] : line[side]) || 0;
  const sum = (side) => lines.reduce((total, line) => total + amountOf(line, side), 0);
  const currencies = [...new Set(lines.map((l) => l.currency).filter(Boolean))];
  // The date most lines carry = the journal's transaction date; only a line that differs shows its own.
  const journalDate = (lines[0]?.trans_date || '').slice(0, 10);
  const linesEditable = editable && allowEditLines;
  const widthStyle = (key) => (widths[key] ? { width: widths[key], minWidth: widths[key], maxWidth: widths[key] } : undefined);

  const renderCell = (column, line, ctx) => {
    const vendorLine = counterparty && line.counterparty_line;
    if (vendorLine && column.key === 'account') {
      return <VendorAccountCell key={column.id} line={line} counterparty={counterparty} width={widths.account} />;
    }
    if (vendorLine && column.kind === 'name') {
      // AX / directory name while the line still carries that vendor; else the name picked with the code.
      const sameCode = String(counterparty.value || '').toUpperCase() === String(line.account || '').toUpperCase();
      const name = (sameCode && accountNameOf(line, ctx.values, ctx.erpLine)) || counterparty.name;
      return (
        <td key={column.id} className="payment-journal-cell payment-journal-name-cell" style={nameCellStyle(widths.account_name)}>
          <span className={`payment-journal-readonly ${name ? "" : "payment-journal-name-empty"}`} title={name || undefined}>{name || '—'}</span>
        </td>
      );
    }
    if (column.kind === 'amount') {
      return <AmountCell key={column.id} line={line} side={column.key} values={ctx.values} editable={linesEditable} onChange={onChange} />;
    }
    if (column.kind === 'name') return <NameCell key={column.id} line={line} values={ctx.values} erpLine={ctx.erpLine} width={widths.account_name} />;
    if (column.kind === 'summary') {
      return <SummaryCell key={column.id} group={column.group} line={line} values={ctx.values} changes={ctx.changes}
        createOnly={createOnly} onExpand={() => toggle(column.group.id)} />;
    }
    const bankLine = line.offset_by_bank_line ? lines.find((l) => l.bank_line && l.line_num === line.offset_by_bank_line) : undefined;
    return <FieldCell key={column.id} field={FIELD_BY_KEY[column.key]} width={widths[column.key]} line={line} values={ctx.values}
      bankLine={bankLine} bankValues={bankLine ? values[lineId(bankLine)] : undefined}
      erpLine={ctx.erpLine} change={ctx.changes.find((c) => c.field === column.key)} createOnly={createOnly.includes(column.key)}
      accountTypes={accountTypes} taxGroups={taxGroups} editable={editable} requestId={requestId} onChange={onChange} nameUnder={!hasNameColumn} />;
  };

  return (
    <div className="payment-journal-table-wrap">
      <table className="payment-journal-table" aria-label="Journal lines" style={{ '--journal-line-width': `${widths.line}px` }}>
        <thead>
          <tr>
            <th rowSpan={2} scope="col" className="payment-journal-line-cell" aria-label="Line">
              Line
              <span className="payment-journal-resize" {...handleProps('line', 'Line')} />
            </th>
            {pinnedAmounts && AMOUNT_KEYS.map((key) => (
              <th key={key} rowSpan={2} scope="col" className="payment-journal-num">{columnDef(key).label}</th>
            ))}
            {groups.map((group) => {
              const keys = columnsOf(group);
              return (
                <th key={group.id} scope="colgroup" colSpan={keys ? keys.length : 1}
                  className={`payment-journal-group-head payment-journal-group-${group.id}`}>
                  {group.collapsible ? (
                    <button type="button" aria-expanded={!!open[group.id]} onClick={() => toggle(group.id)}
                      title={open[group.id] ? `Fold ${group.label} into one column` : `Show every ${group.label} field`}>
                      {open[group.id] ? <FaChevronLeft aria-hidden="true" size={9} /> : <FaChevronRight aria-hidden="true" size={9} />}
                      {group.label}
                    </button>
                  ) : group.label}
                </th>
              );
            })}
          </tr>
          <tr>
            {columns.filter((c) => !c.pinned).map((column) => {
              if (column.kind === 'summary') return <th key={column.id} scope="col" className="payment-journal-field-head">Filled</th>;
              const def = columnDef(column.key);
              return (
                <th key={column.id} scope="col" style={widthStyle(column.key)} aria-label={def.label}
                  className={`payment-journal-field-head ${column.kind === 'amount' ? 'payment-journal-num' : ''}`}>
                  {def.label}
                  {widths[column.key] && <span className="payment-journal-resize" {...handleProps(column.key, def.label)} />}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const id = lineId(line);
            const ctx = {
              values: values[id] || {},
              erpLine: erpLines ? (erpLines.find((e) => e.line_num === line.line_num) || null) : undefined,
              changes: pending.filter((c) => c.line_num === line.line_num),
            };
            const rowKind = line.manual ? "payment-journal-row-manual" : line.account_type === "Vendor" ? "payment-journal-row-vendor" : "payment-journal-row-ledger";
            return (
              <tr key={id} aria-label={`Journal line ${line.line_num}`} className={rowKind}>
                <LineCell line={line} erpLine={ctx.erpLine} journalDate={journalDate}
                  onRemove={linesEditable && onRemoveLine ? () => onRemoveLine(id) : undefined} />
                {columns.map((column) => renderCell(column, line, ctx))}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {linesEditable && (
            <tr className="payment-journal-add-row">
              <th scope="row" className="payment-journal-line-cell">
                <button type="button" onClick={onAddLine} className="payment-journal-add-line">
                  <FaPlus aria-hidden="true" size={10} /> Add line
                </button>
              </th>
              <td colSpan={columns.length} className="payment-journal-add-hint">
                A manual line needs a Debit or a Credit; the journal must still balance.
              </td>
            </tr>
          )}
          {lines.length > 1 && (
            <tr>
              <th scope="row" className="payment-journal-line-cell">Total{currencies.length === 1 ? ` (${currencies[0]})` : ''}</th>
              {columns.map((column) => (column.kind === 'amount'
                ? <td key={column.id} className="payment-journal-num">{money(sum(column.key))}</td>
                : <td key={column.id} />))}
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
};

JournalLinesTable.propTypes = {
  // { value, name, onChange(code, name), editable, requestId, placeholder } — the journal's
  // vendor, picked on the Vendor line. Absent for PO-based and employee journals.
  counterparty: PropTypes.object,
  journalType: PropTypes.string,
  lines: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  erpLines: PropTypes.array,
  pending: PropTypes.array.isRequired,
  createOnly: PropTypes.array.isRequired,
  accountTypes: PropTypes.array.isRequired,
  taxGroups: PropTypes.array.isRequired,
  editable: PropTypes.bool,
  allowEditLines: PropTypes.bool,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  onAddLine: PropTypes.func,
  onRemoveLine: PropTypes.func,
};

export default JournalLinesTable;
